import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getTourDetails,
  getTourSealNumber,
  getRiderList,
  clearTourDetails,
  clearRiderDetails,
  clearTourSealNumberDetails,
} from "../redux/actions/tourActions";
import { Button, DatePicker, Form, Input, Space, Select, Spin } from "antd";
import moment from "moment";
import queryString from "query-string";
import debounce from "lodash/debounce";
import SealedValueTable from "./sealedValueTable/SealedValueTable";

const layout = {
  style: { margin: "24px" },
  labelCol: {
    span: 8,
  },
  wrapperCol: {
    span: 16,
  },
};
const tailLayout = {
  wrapperCol: {
    offset: 8,
    span: 16,
  },
};

const AppContent = () => {
  const { Option } = Select;
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const tours = useSelector((state) => state?.tours?.allTours);
  const riders = useSelector((state) => state?.riders?.allRiders);
  const { authToken, clientId } = queryString.parse(location.search);

  const [tourDisable, setTourDisable] = useState(true);
  const [isFetchingTour, setIsFetchingTour] = useState(false);
  const [enableTable, setEnableTabel] = useState(false);
  const [tourDetails, setTourDetails] = useState(false);

  useEffect(() => {
    if (tourDisable) dispatch(clearTourDetails());
  }, [tourDisable, dispatch]);

  const handleError = useCallback(
    (error = {}) => {
      const { message = "", data = {} } = error;
      navigate(`/error-fallback${location.search}`, {
        state: { from: location, message, data },
      });
    },
    [navigate, location]
  );

  useEffect(() => {
    const authParams = { clientId, authToken };
    dispatch(clearRiderDetails());
    dispatch(getRiderList(authParams, navigate, handleError));
  }, [authToken, clientId, dispatch, handleError, navigate]);

  const debouncedGetTourData = useMemo(() => {
    return debounce((riderId) => {
      async function fetchData() {
        try {
          setIsFetchingTour(true);
          const filter = {
            filters: [
              {
                name: "date",
                operation: "EQUALS",
                value: moment(form.getFieldValue("planDate")).format(
                  "YYYY-MM-DD"
                ),
              },
              {
                name: "fleetInfo.riderId.riderId",
                operation: "EQUALS",
                value: form.getFieldValue("riderId"),
              },
            ],
            page: 1,
            size: 25,
          };
          const authParams = { clientId, authToken, filter };
          const checkAndFetchAllTours = (data) => {
            if (data.status === "success") {
              if (
                data?.response?.currentPage === data?.response?.numberOfPages
              ) {
                setIsFetchingTour(false);
                return;
              }
              filter.page = data?.response?.currentPage + 1;
              dispatch(
                getTourDetails(
                  authParams,
                  navigate,
                  handleError,
                  checkAndFetchAllTours
                )
              );
            }
          };
          dispatch(clearTourDetails());
          dispatch(
            getTourDetails(
              authParams,
              navigate,
              handleError,
              checkAndFetchAllTours
            )
          );
        } catch (error) {
          console.log("Error in fetching the tours", error);
        }
      }
      fetchData();
    }, 1000);
  }, [clientId, authToken, dispatch, form, handleError, navigate]);

  const resetFrom = () => {
    form.resetFields();
    setTourDisable(true);
    setEnableTabel(false);
  };

  const checkForTourSelect = () => {
    if (form.getFieldValue("riderId") && form.getFieldValue("planDate")) {
      setTourDisable(false);
      form.setFieldValue("tourId", "");
      debouncedGetTourData();
      return;
    }
    setTourDisable(true);
  };

  const onFinish = (values) => {
    const tour = tours.filter((tour, index) => tour?.id === values.tourId);
    const data = {
      date: moment(form.getFieldValue("planDate")).format("YYYY-MM-DD"),
      riderId: values.riderId,
      tourId: values.tourId,
      tour: tour?.[0],
      numberOfGates: values.gates,
    };

    const authParams = {
      authToken,
      clientId,
      tourId: values.tourId,
    };

    const handleFinish = (finishStatus) => {
      if (finishStatus.status === "success") {
        setTourDetails(data);
        setEnableTabel(true);
      }
    };

    dispatch(clearTourSealNumberDetails());

    dispatch(
      getTourSealNumber(authParams, false, navigate, handleError, handleFinish)
    );
  };

  return (
    <>
      <Form
        {...layout}
        form={form}
        name="input-page"
        size="middle"
        onFinish={onFinish}
      >
        <React.Fragment>
          <Form.Item
            shouldUpdate
            label="Plan Date"
            name="planDate"
            rules={[
              {
                required: true,
                message: "Please input your plan date!",
              },
            ]}
          >
            <DatePicker
              style={{ width: "187px" }}
              onChange={() => checkForTourSelect()}
            />
          </Form.Item>
          <Form.Item
            shouldUpdate
            label="Rider Id"
            name="riderId"
            rules={[
              {
                required: true,
                message: "Please select riderId !",
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="children"
              style={{ width: "187px" }}
              onChange={(e) => {
                checkForTourSelect();
              }}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {riders &&
                riders.map((rider, index) => (
                  <Option key={index} value={rider}>
                    {rider}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Select Tour"
            name="tourId"
            rules={[
              {
                required: true,
                message: "Please select an tour!",
              },
            ]}
          >
            <Select style={{ width: "187px" }}>
              {isFetchingTour ? (
                <Select.Option value="0" disabled>
                  <Spin size="small" /> Loading tours......
                </Select.Option>
              ) : (
                !tourDisable &&
                tours &&
                tours.map((tour, index) => (
                  <Select.Option key={index} value={tour?.id}>
                    {tour?.tourName}
                  </Select.Option>
                ))
              )}
            </Select>
          </Form.Item>
          <Form.Item
            shouldUpdate
            label="No. of gates"
            name="gates"
            rules={[
              {
                required: true,
                message: "Please input number of gates!",
              },
              {
                validator: (_, value) => {
                  if (value > 0 && value < 11) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    "Number of gates must be greater than 0 and lesser than 10!"
                  );
                },
              },
            ]}
          >
            <Space size="middle">
              <>
                <Input type="number" />
              </>
            </Space>
          </Form.Item>
        </React.Fragment>
        <Form.Item {...tailLayout}>
          <Button type="primary" htmlType="submit">
            OK
          </Button>
          <Button
            style={{ marginLeft: "24px" }}
            htmlType="button"
            onClick={() => resetFrom()}
          >
            Reset
          </Button>
        </Form.Item>
      </Form>
      {enableTable && (
        <SealedValueTable
          tourDetails={tourDetails}
          isRider={false}
          handleError={handleError}
          clientId={clientId}
          authToken={authToken}
          setEnableTabel={setEnableTabel}
        ></SealedValueTable>
      )}
    </>
  );
};

export default AppContent;
